import axios from 'axios';
import type { AxiosError, AxiosInstance } from 'axios';

export type AuthHeaders = Record<string, string>;

type ApiErrorBody = {
  message?: unknown;
  details?: unknown;
  errors?: unknown;
};

const DEFAULT_TIMEOUT_MS = 60_000;

export const createApiHttpClient = (): AxiosInstance =>
  axios.create({
    timeout: DEFAULT_TIMEOUT_MS
  });

const stringifyErrorPart = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const messages = value
      .map((item) =>
        item && typeof item === 'object' && 'message' in item
          ? stringifyErrorPart((item as { message?: unknown }).message)
          : stringifyErrorPart(item)
      )
      .filter(Boolean);

    return messages.length ? messages.join('; ') : null;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const readAxiosErrorBody = (error: AxiosError): ApiErrorBody => {
  const data = error.response?.data;
  return data && typeof data === 'object' ? (data as ApiErrorBody) : {};
};

const isGenericAxiosTransportMessage = (message: string): boolean => {
  const normalized = message.trim();
  return (
    normalized === '' ||
    /^Network Error$/i.test(normalized) ||
    /^Request failed$/i.test(normalized) ||
    /^Request failed with status code \d+$/i.test(normalized) ||
    /^timeout of \d+ms exceeded$/i.test(normalized)
  );
};

const readObjectErrorBody = (error: unknown): ApiErrorBody => {
  if (!error || typeof error !== 'object' || Array.isArray(error)) {
    return {};
  }

  const body = (error as { body?: unknown }).body;
  return body && typeof body === 'object' && !Array.isArray(body) ? (body as ApiErrorBody) : {};
};

export const readApiErrorBody = (error: unknown): unknown =>
  axios.isAxiosError(error)
    ? error.response?.data
    : error && typeof error === 'object' && !Array.isArray(error)
      ? (error as { body?: unknown }).body
      : undefined;

export const readApiErrorMessage = (
  error: unknown,
  fallback = 'Ошибка запроса'
): string => {
  if (axios.isAxiosError(error)) {
    const body = readAxiosErrorBody(error);
    const message = stringifyErrorPart(body.message);
    const details = stringifyErrorPart(body.details ?? body.errors);
    const combined = [message, details].filter(Boolean).join(': ');

    if (combined) {
      return combined;
    }

    return isGenericAxiosTransportMessage(error.message) ? fallback : error.message;
  }

  const body = readObjectErrorBody(error);
  const message = stringifyErrorPart(body.message);
  const details = stringifyErrorPart(body.details ?? body.errors);
  const combined = [message, details].filter(Boolean).join(': ');
  if (combined) {
    return combined;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return fallback;
};

export const isApiErrorWithMessage = (error: unknown, needle: string): boolean =>
  readApiErrorMessage(error, '').toLowerCase().includes(needle.toLowerCase());
